/**
 * Threat Log — the incident explorer rendered as the 4th tab inside Origin
 * Shield. Per-category mitigation counts are shown as clickable cards
 * (`/waf/threat/summary`); selecting one filters the paginated incident table
 * (`/waf/threat/incidents`) by that category. Each row opens a detail dialog
 * with the full event + matched WAF/CRS rules. Search narrows BOTH the cards
 * and the table, by incident id (`request_id`) or client IP.
 */
import { useMemo, useState, type ReactNode } from 'react';
import { Search, ShieldAlert } from 'lucide-react';
import {
  Input,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Badge,
  Skeleton,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogFooter,
  Button,
} from '@vnetwork/ui/primitives';
import {
  DataTable,
  EmptyState,
  Pagination,
  StatusPill,
  type ColumnDef,
  type StatusTone,
} from '@vnetwork/ui/data';
import {
  useWafThreatReportSummary,
  useWafThreatIncidents,
  fmtEpoch,
  type WafThreatIncident,
} from '@/api/waf';
import { CardGrid, RefreshButton } from '../../_shared/AnalyticsKit';
import { formatNum } from '../../_shared/analytics-format';
import { useDebounce } from '../../shared';
import type { ResolvedRange } from './WafRangeControl';
import styles from './WafAnalytics.module.css';

type SearchBy = 'request_id' | 'ip';
const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

/** Threat-log country: "00" is the vendor's unresolved-geo sentinel. */
const displayCountry = (c?: string): string => (c === '00' ? 'Unidentified' : c ?? '');

/** Incident IDs are lowercase alphanumeric; IP search expects a dotted IPv4. */
const INCIDENT_ID_RE = /^[a-z0-9]+$/;
const IPV4_RE = /^(25[0-5]|2[0-4]\d|1?\d?\d)(\.(25[0-5]|2[0-4]\d|1?\d?\d)){3}$/;
const isValidSearch = (by: SearchBy, v: string) =>
  by === 'ip' ? IPV4_RE.test(v) : INCIDENT_ID_RE.test(v);

/** Action → pill tone (vendor-cased: Block / Challenge / Pass / Allow). */
function actionTone(action: string): StatusTone {
  const a = (action ?? '').toLowerCase();
  if (a.includes('block') || a.includes('deny') || a.includes('drop')) return 'danger';
  if (a.includes('challenge') || a.includes('captcha') || a.includes('js')) return 'warning';
  if (a.includes('pass') || a.includes('allow')) return 'success';
  return 'neutral';
}

const mono = { fontFamily: 'var(--vn-font-mono, ui-monospace, monospace)', fontSize: 13 };

export function WafThreatLogPanel({
  serviceId,
  domain,
  range,
}: {
  serviceId: string;
  domain: string;
  range: ResolvedRange;
}) {
  const [searchBy, setSearchBy] = useState<SearchBy>('request_id');
  const [searchInput, setSearchInput] = useState('');
  const search = useDebounce(searchInput, 400);
  const [categories, setCategories] = useState<string[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [detail, setDetail] = useState<WafThreatIncident | null>(null);

  const hasScope = !!domain && !!range;
  // Search maps to exactly one narrowing param (request_id | ip), applied to both
  // calls — but ONLY once the (debounced) value matches the expected format.
  const searchTrimmed = search.trim();
  const searchOk = isValidSearch(searchBy, searchTrimmed);
  const searchParam =
    searchTrimmed && searchOk
      ? searchBy === 'ip'
        ? { ip: searchTrimmed }
        : { request_id: searchTrimmed }
      : {};
  // Live invalid state on the field (immediate input, not debounced).
  const inputTrimmed = searchInput.trim();
  const inputInvalid = inputTrimmed.length > 0 && !isValidSearch(searchBy, inputTrimmed);

  const baseFilter = {
    domain,
    start_time: range?.start_time ?? 0,
    end_time: range?.end_time ?? 0,
    ...searchParam,
  };

  const summaryQ = useWafThreatReportSummary(serviceId, baseFilter, hasScope);
  const incidentsQ = useWafThreatIncidents(
    serviceId,
    { ...baseFilter, category: categories.length ? categories.join(',') : undefined, page, page_size: pageSize },
    hasScope,
  );

  const cards = summaryQ.data ?? [];
  const rows = incidentsQ.data?.items ?? [];
  const pagination = incidentsQ.data?.pagination;
  const total = pagination?.total ?? rows.length;
  const pageCount = pagination?.total_pages ?? 1;

  const summaryLoading = !hasScope || summaryQ.isPending || summaryQ.isFetching;
  const tableLoading = !hasScope || incidentsQ.isPending || incidentsQ.isFetching;

  const toggleCategory = (name: string) => {
    setCategories((prev) => (prev.includes(name) ? prev.filter((c) => c !== name) : [...prev, name]));
    setPage(1);
  };

  const columns = useMemo<ColumnDef<WafThreatIncident, unknown>[]>(
    () => [
      {
        id: 'time',
        accessorKey: 'time',
        header: 'Date',
        cell: ({ row }) => fmtEpoch(row.original.time) || row.original.request_time || '—',
      },
      {
        id: 'domain',
        accessorKey: 'domain',
        header: 'Request Domain',
        cell: ({ row }) => row.original.domain || row.original.request_host || '—',
      },
      {
        id: 'category',
        accessorKey: 'category',
        header: 'Category',
        cell: ({ row }) =>
          row.original.category ? (
            <Badge tone="info" appearance="subtle" size="sm">
              {row.original.category}
            </Badge>
          ) : (
            '—'
          ),
      },
      {
        id: 'ip',
        accessorKey: 'ip',
        header: 'IP',
        cell: ({ row }) => <span style={mono}>{row.original.ip || '—'}</span>,
      },
      {
        id: 'country',
        accessorKey: 'country',
        header: 'Country',
        cell: ({ row }) => displayCountry(row.original.country) || '—',
      },
      {
        id: 'action',
        accessorKey: 'action',
        header: 'Action',
        cell: ({ row }) =>
          row.original.action ? (
            <StatusPill tone={actionTone(row.original.action)}>{row.original.action}</StatusPill>
          ) : (
            '—'
          ),
      },
    ],
    [],
  );

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      {/* Search row */}
      <div className={styles.controls}>
        <div style={{ display: 'grid', gap: 4 }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--vn-space-2)' }}>
            <Input
              placeholder={searchBy === 'ip' ? 'e.g. 212.132.119.238' : 'Search by incident ID'}
              startSlot={<Search size={14} />}
              value={searchInput}
              invalid={inputInvalid}
              aria-invalid={inputInvalid}
              onChange={(e) => {
                setSearchInput(e.target.value);
                setPage(1);
              }}
              style={{ width: 280 }}
            />
            <Select
              value={searchBy}
              onValueChange={(v) => {
                setSearchBy(v as SearchBy);
                setPage(1);
              }}
            >
              <SelectTrigger aria-label="Search by" style={{ width: 150 }}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="request_id">Incident ID</SelectItem>
                <SelectItem value="ip">IP</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {inputInvalid && (
            <span style={{ fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-danger-text)' }}>
              {searchBy === 'ip'
                ? 'Enter a valid IPv4 address (e.g. 212.132.119.238).'
                : 'Incident ID must be lowercase letters and digits only.'}
            </span>
          )}
        </div>
        <div className={styles.controlsRight}>
          <RefreshButton
            onRefresh={() => {
              summaryQ.refetch();
              incidentsQ.refetch();
            }}
          />
        </div>
      </div>

      {/* Category cards */}
      {summaryLoading ? (
        <CardGrid min={180}>
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} style={{ height: 72 }} />
          ))}
        </CardGrid>
      ) : cards.length === 0 ? (
        <EmptyState
          icon={<ShieldAlert size={20} />}
          title="No mitigations yet"
          description="No mitigations recorded in the selected range."
        />
      ) : (
        <CardGrid min={180}>
          {cards.map((c) => {
            const active = categories.includes(c.category);
            return (
              <button
                key={c.category}
                type="button"
                className={`${styles.catCard} ${active ? styles.catCardActive : ''}`}
                onClick={() => toggleCategory(c.category)}
                aria-pressed={active}
              >
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 'var(--vn-space-2)',
                    fontSize: 'var(--vn-font-size-sm)',
                    color: 'var(--vn-fg-muted)',
                  }}
                >
                  <ShieldAlert size={14} /> {c.category}
                </span>
                <span style={{ fontSize: 22, fontWeight: 600 }}>{formatNum(c.total_request)}</span>
              </button>
            );
          })}
        </CardGrid>
      )}

      {/* Incident table */}
      {tableLoading ? (
        <Skeleton style={{ height: 320 }} />
      ) : (
        <>
          <DataTable
            columns={columns}
            data={rows}
            getRowId={(r, i) => r.incident_id || String(i)}
            enableSorting={false}
            onRowClick={(r) => setDetail(r)}
            emptyState={
              categories.length
                ? `No incidents for the selected ${categories.length > 1 ? 'categories' : 'category'} in the selected range.`
                : 'No incidents in the selected range.'
            }
            footer={`Showing ${rows.length ? (page - 1) * pageSize + 1 : 0}–${
              (page - 1) * pageSize + rows.length
            } of ${total}`}
          />
          {(pageCount > 1 || total > PAGE_SIZE_OPTIONS[0]) && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 'var(--vn-space-4)',
                flexWrap: 'wrap',
              }}
            >
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--vn-space-2)' }}>
                <label
                  htmlFor="waf-threat-page-size"
                  style={{ color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}
                >
                  Rows per page
                </label>
                <Select
                  value={String(pageSize)}
                  onValueChange={(v) => {
                    setPageSize(Number(v));
                    setPage(1);
                  }}
                >
                  <SelectTrigger id="waf-threat-page-size" size="sm" style={{ width: 72 }}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PAGE_SIZE_OPTIONS.map((s) => (
                      <SelectItem key={s} value={String(s)}>
                        {s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {pageCount > 1 && (
                <Pagination
                  page={page}
                  pageCount={pageCount}
                  onPageChange={setPage}
                  summary={incidentsQ.isFetching ? 'Loading…' : `Page ${page} of ${pageCount}`}
                />
              )}
            </div>
          )}
        </>
      )}

      <WafThreatLogDetailDialog
        incident={detail}
        open={!!detail}
        onOpenChange={(o) => !o && setDetail(null)}
      />
    </div>
  );
}

/** Definition row: muted label over (or beside) a value. */
function DefRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div style={{ display: 'grid', gap: 2 }}>
      <span
        style={{
          fontSize: 'var(--vn-font-size-xs)',
          color: 'var(--vn-fg-muted)',
          textTransform: 'uppercase',
          letterSpacing: '0.03em',
        }}
      >
        {label}
      </span>
      <span style={{ fontSize: 'var(--vn-font-size-sm)', wordBreak: 'break-word' }}>{value || '—'}</span>
    </div>
  );
}

function WafThreatLogDetailDialog({
  incident,
  open,
  onOpenChange,
}: {
  incident: WafThreatIncident | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>Incident detail</DialogTitle>
          <DialogDescription>
            {incident ? `${incident.action || 'Event'} · ${fmtEpoch(incident.time)}` : ''}
          </DialogDescription>
        </DialogHeader>
        <DialogBody style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
          {incident && (
            <>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                  gap: 'var(--vn-space-4)',
                }}
              >
                <DefRow label="Incident ID" value={<span style={mono}>{incident.incident_id}</span>} />
                <DefRow label="Category" value={incident.category} />
                <DefRow
                  label="Action"
                  value={
                    incident.action ? (
                      <StatusPill tone={actionTone(incident.action)}>{incident.action}</StatusPill>
                    ) : (
                      '—'
                    )
                  }
                />
                <DefRow label="Severity" value={incident.severity} />
                <DefRow label="Module" value={incident.module} />
                <DefRow label="Score" value={incident.msg?.score} />
                <DefRow label="Domain" value={incident.domain || incident.request_host} />
                <DefRow label="Country" value={displayCountry(incident.country)} />
                <DefRow label="Client IP" value={<span style={mono}>{incident.ip}</span>} />
                <DefRow
                  label="X-Forwarded-For"
                  value={<span style={mono}>{incident.http_x_forwarded_for}</span>}
                />
                <DefRow
                  label="Request"
                  value={
                    <span style={mono}>
                      {incident.request_method} {incident.scheme}://{incident.request_host}
                      {incident.uri}
                      {incident.query_string ? `?${incident.query_string}` : ''}
                    </span>
                  }
                />
                <DefRow label="Protocol" value={incident.server_protocol} />
                <DefRow label="User agent" value={incident.http_user_agent} />
              </div>

              {incident.msg?.hit?.length ? (
                <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
                  <span
                    style={{
                      fontSize: 'var(--vn-font-size-sm)',
                      fontWeight: 600,
                    }}
                  >
                    Matched rules ({incident.msg.hit.length})
                  </span>
                  <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
                    {incident.msg.hit.map((h, i) => (
                      <div
                        key={`${h.id}-${i}`}
                        style={{
                          border: '1px solid var(--vn-border-default)',
                          borderRadius: 'var(--vn-radius-md)',
                          padding: 'var(--vn-space-3)',
                          display: 'grid',
                          gap: 4,
                        }}
                      >
                        <span
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 'var(--vn-space-2)',
                            flexWrap: 'wrap',
                          }}
                        >
                          <Badge tone="info" appearance="subtle" size="sm">
                            {h.category}
                          </Badge>
                          <span style={{ fontSize: 13 }}>{h.msg}</span>
                          <span style={{ fontSize: 12, color: 'var(--vn-fg-muted)' }}>
                            #{h.id} · severity {h.severity}
                          </span>
                        </span>
                        {(h.match_key || h.match_val) && (
                          <span style={{ ...mono, color: 'var(--vn-fg-muted)' }}>
                            {h.match_key}
                            {h.match_val ? `: ${h.match_val}` : ''}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}
            </>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default WafThreatLogPanel;
